// @ts-check
// Cadastro de instituição no portal (/sign-up), sem senha: o e-mail de quem cadastra é confirmado com um código de
// 6 dígitos antes de POST /auth/sign-up; o redirectUrl devolvido abre a sessão no subdomínio da instituição. O código
// de acesso pode chegar pelo link do painel de suporte (#code= ou ?code=), e quem já tem conta (agências, redes) cria
// outra instituição com o mesmo e-mail (contrato 08 de cobrança e operação, seções 7.1 e 7.2). Nome e sobrenome só
// aparecem depois do envio do código, quando a resposta diz que a conta é nova (regras em lib/sign-up-form.js).
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { requestSignUpEmailCode, signUp } from '../../data/rest.js';
import { ApiError, getErrorMessage } from '../../data/api-client.js';
import { MainButton } from '../../ui/button.js';
import { Checkbox } from '../../ui/feedback.js';
import { showSuccessSnackbar } from '../../ui/snackbar.js';
import { AuthLayout, AuthTextField } from './auth-layout.js';
import { EMAIL_CODE_LENGTH, EmailCodeField, RESEND_COOLDOWN_SECONDS, ResendCodeButton, useCooldownSeconds } from './email-code-field.js';
import { Link, navigate } from '../../app/router.js';
import { readSignUpCodeFromLocation, signUpCodeEnding } from '../../lib/sign-up-code-link.js';
import { asksSignUpName, canCreateInstitution, canSendSignUpCode, isSignUpNameMissing } from '../../lib/sign-up-form.js';
import { Icon } from '../../ui/icon.js';
import { t } from '../../i18n/index.js';

// mesma regra do backend (src/core/tenant-repository.js): 3 a 40 caracteres
const SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]{1,38}[a-z0-9])$/;
const SLUG_MAXIMUM_LENGTH = 40;
const SLUG_ERROR_CODES = new Set(['INVALID_SLUG', 'SLUG_ALREADY_TAKEN']);
const ACCESS_CODE_ERROR_CODES = new Set(['INVALID_ACCESS_CODE', 'ACCESS_CODE_EMAIL_MISMATCH']);

/**
 * Slug a partir do nome da instituição: sem acentos, minúsculas e hífens, até 40 caracteres.
 * @param {string} name
 */
export function toSlug(name) {
  return name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, SLUG_MAXIMUM_LENGTH)
    .replace(/-+$/g, '');
}

export function SignUpPage() {
  // lido uma vez, antes de o endereço ser limpo
  const [linkAccessCode] = useState(() => readSignUpCodeFromLocation(window.location).code);
  const [isAccessCodeFromLink, setIsAccessCodeFromLink] = useState(linkAccessCode !== null);
  const [form, setForm] = useState({
    institutionName: '', slug: '', firstName: '', lastName: '', email: '', accessCode: linkAccessCode ?? '', seedDemoData: false,
  });
  const [accessCodeError, setAccessCodeError] = useState(/** @type {string | null} */ (null));
  // o e-mail já tem conta: a instituição nova entra junto das outras e o nome da conta não muda
  const [isExistingAccount, setIsExistingAccount] = useState(false);
  const [isSlugEdited, setIsSlugEdited] = useState(false);
  const [codeSentTo, setCodeSentTo] = useState(/** @type {string | null} */ (null));
  const [emailCode, setEmailCode] = useState('');
  const [isDevCode, setIsDevCode] = useState(false);
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const [codeError, setCodeError] = useState(/** @type {string | null} */ (null));
  const [nameError, setNameError] = useState(/** @type {string | null} */ (null));
  const [slugServerError, setSlugServerError] = useState(/** @type {string | null} */ (null));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendAvailableAt, setResendAvailableAt] = useState(/** @type {number | null} */ (null));
  const resendSecondsLeft = useCooldownSeconds(resendAvailableAt);

  useEffect(() => {
    document.title = t('auth.signUp.documentTitle');
    // o código não fica na barra de endereço, no histórico nem em quem copiar a URL
    const { code, cleanUrl } = readSignUpCodeFromLocation(window.location);
    if (code !== null) navigate(cleanUrl, { replace: true });
  }, []);

  const resetEmailCode = () => {
    setCodeSentTo(null);
    setEmailCode('');
    setIsDevCode(false);
    setCodeError(null);
    setResendAvailableAt(null);
    setIsExistingAccount(false);
    setNameError(null);
  };

  const update = (/** @type {string} */ key) => (/** @type {any} */ value) => {
    if (key === 'email' && codeSentTo !== null && value.trim().toLowerCase() !== codeSentTo) resetEmailCode();
    if (key === 'slug' || (key === 'institutionName' && !isSlugEdited)) setSlugServerError(null);
    if (key === 'accessCode' || key === 'email') setAccessCodeError(null);
    if (key === 'firstName') setNameError(null);
    setForm((current) => ({
      ...current,
      [key]: value,
      ...(key === 'institutionName' && !isSlugEdited ? { slug: toSlug(value) } : {}),
    }));
  };

  const email = form.email.trim();
  const slugError = form.slug && !SLUG_PATTERN.test(form.slug) ? t('auth.signUp.slugInvalid') : slugServerError;
  const isCodeSent = codeSentTo !== null;
  const formState = { isSlugValid: !slugError, isCodeSent, isExistingAccount };
  const canSendCode = canSendSignUpCode(form, formState);
  const asksName = asksSignUpName(formState);
  const isNameMissing = isSignUpNameMissing(form, formState);

  /** @param {{ sent?: boolean, devCode?: string, existingAccount?: boolean }} response */
  const applyCodeResponse = (response) => {
    setResendAvailableAt(Date.now() + RESEND_COOLDOWN_SECONDS * 1000);
    setIsExistingAccount(response?.existingAccount === true);
    if (response?.devCode) {
      setEmailCode(response.devCode);
      setIsDevCode(true);
    }
  };

  /** O erro do código de acesso fica junto do campo; os outros, no aviso do formulário. @param {unknown} requestError */
  const showRequestError = (requestError) => {
    const errorCode = requestError instanceof ApiError ? requestError.code : null;
    if (errorCode && ACCESS_CODE_ERROR_CODES.has(errorCode)) setAccessCodeError(getErrorMessage(requestError));
    else setError(getErrorMessage(requestError));
  };

  /** @param {{ isResend: boolean }} options */
  const sendEmailCode = async ({ isResend }) => {
    setError(null);
    setCodeError(null);
    if (isResend) setIsResending(true);
    else setIsSubmitting(true);
    try {
      const response = await requestSignUpEmailCode({ email, accessCode: form.accessCode.trim() });
      setCodeSentTo(email.toLowerCase());
      applyCodeResponse(response);
      if (isResend) showSuccessSnackbar(t('auth.emailCode.resent', { email }));
    } catch (sendError) {
      showRequestError(sendError);
    } finally {
      setIsResending(false);
      setIsSubmitting(false);
    }
  };

  /** @param {string} code */
  const createInstitution = async (code) => {
    if (code.length !== EMAIL_CODE_LENGTH || isSubmitting) return;
    // o código pode ser digitado antes do nome: avisa no campo em vez de não fazer nada. O foco espera o próximo
    // quadro de animação porque o campo do código ainda move o foco para a caixa seguinte depois de chamar onComplete.
    if (isNameMissing) {
      setNameError(t('auth.signUp.firstNameRequired'));
      requestAnimationFrame(() => /** @type {HTMLInputElement | null} */ (document.querySelector('.auth-form input[name="firstName"]'))?.focus());
      return;
    }
    if (!canCreateInstitution(form, formState)) return;
    setError(null);
    setCodeError(null);
    setIsSubmitting(true);
    try {
      const body = await signUp({
        institutionName: form.institutionName.trim(),
        slug: form.slug,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email,
        emailCode: code,
        seedDemoData: form.seedDemoData,
        accessCode: form.accessCode.trim(),
      });
      const redirectUrl = body?.redirectUrl;
      if (redirectUrl && /^https?:\/\//.test(redirectUrl)) window.location.assign(redirectUrl);
      else navigate(redirectUrl ?? '/', { replace: true });
      // mantém o botão em carregamento até a navegação para o host da instituição
      return;
    } catch (signUpError) {
      const errorCode = signUpError instanceof ApiError ? signUpError.code : null;
      if (errorCode === 'INVALID_EMAIL_CODE') {
        setCodeError(getErrorMessage(signUpError));
        setEmailCode('');
        setIsDevCode(false);
      } else if (errorCode && SLUG_ERROR_CODES.has(errorCode)) {
        setSlugServerError(getErrorMessage(signUpError));
      } else {
        showRequestError(signUpError);
      }
    }
    setIsSubmitting(false);
  };

  const submit = (/** @type {Event} */ event) => {
    event.preventDefault();
    if (isCodeSent) createInstitution(emailCode);
    else if (canSendCode) sendEmailCode({ isResend: false });
  };

  return html`<${AuthLayout}
    title=${t('auth.signUp.title')}
    subtitle=${t('auth.signUp.subtitle')}
    footer=${html`<span>${t('auth.signUp.haveAccount')} <${Link} href="/welcome">${t('auth.signUp.signIn')}<//></span>`}
  >
    <form class="auth-form" onSubmit=${submit}>
      <${AuthTextField} label=${t('auth.fields.institutionName')} name="institutionName" value=${form.institutionName} onInput=${update('institutionName')}
        placeholder=${t('auth.fields.institutionNamePlaceholder')} autoFocus=${true} />
      <${AuthTextField} label=${t('auth.fields.slug')} name="slug" value=${form.slug}
        onInput=${(value) => {
          setIsSlugEdited(true);
          update('slug')(value.toLowerCase());
        }}
        placeholder=${t('auth.fields.slugPlaceholder')} error=${slugError} hint=${t('auth.signUp.slugHint', { slug: form.slug || t('auth.fields.slugPlaceholder'), domain: window.location.hostname.replace(/^app\./, '') })} />
      <${AuthTextField} label=${t('auth.fields.email')} name="email" type="email" value=${form.email} onInput=${update('email')} autoComplete="email"
        placeholder=${t('auth.fields.emailPlaceholder')} hint=${isCodeSent ? undefined : t('auth.signUp.emailHint')} />
      ${isExistingAccount
        ? html`<div class="auth-notice" role="status">
            <${Icon} name="IconInfoCircle" size=${16} />
            <span><strong>${t('auth.signUp.existingAccountNotice')}</strong> ${t('auth.signUp.existingAccountNameHint')}</span>
          </div>`
        : null}
      ${asksName
        ? html`<div class="auth-field-group">
            <div class="auth-field-row">
              <${AuthTextField} label=${t('auth.fields.firstName')} name="firstName" value=${form.firstName} onInput=${update('firstName')} autoComplete="given-name"
                error=${nameError} autoFocus=${isNameMissing} />
              <${AuthTextField} label=${t('auth.fields.lastName')} name="lastName" value=${form.lastName} onInput=${update('lastName')} autoComplete="family-name" required=${false} />
            </div>
            <span class="input-hint">${t('auth.signUp.newAccountNameHint')}</span>
          </div>`
        : null}
      ${isAccessCodeFromLink
        ? html`<div class="auth-field">
            <span class="input-label">${t('auth.fields.accessCode')}</span>
            <div class="auth-access-code-link" data-error=${accessCodeError ? 'true' : undefined}>
              <${Icon} name="IconKey" size=${16} />
              <span class="auth-access-code-link-text">${t('auth.signUp.accessCodeFromLink', { hint: signUpCodeEnding(form.accessCode) })}</span>
              <button type="button" class="auth-link-button" onClick=${() => {
                setIsAccessCodeFromLink(false);
                setAccessCodeError(null);
                update('accessCode')('');
                requestAnimationFrame(() => /** @type {HTMLInputElement | null} */ (document.querySelector('.auth-form input[name="accessCode"]'))?.focus());
              }}>${t('auth.signUp.changeAccessCode')}</button>
            </div>
            ${accessCodeError ? html`<span class="input-hint" data-danger="true" role="alert">${accessCodeError}</span>` : null}
          </div>`
        : html`<${AuthTextField} label=${t('auth.fields.accessCode')} name="accessCode" value=${form.accessCode} onInput=${update('accessCode')}
            autoComplete="off" required=${false} hint=${t('auth.signUp.accessCodeHint')} error=${accessCodeError} />`}
      <div class="auth-checkbox">
        <${Checkbox} checked=${form.seedDemoData} onChange=${(checked) => update('seedDemoData')(checked)} ariaLabel=${t('auth.signUp.seedDemoData')} />
        <span onClick=${() => update('seedDemoData')(!form.seedDemoData)}>${t('auth.signUp.seedDemoData')}</span>
      </div>
      ${isCodeSent
        ? html`<div class="auth-code-panel">
            <p class="auth-code-panel-text">${t('auth.signUp.codeSentTo')} <strong>${email}</strong></p>
            <${EmailCodeField}
              value=${emailCode}
              onInput=${(value) => {
                setEmailCode(value);
                setIsDevCode(false);
                if (codeError) setCodeError(null);
              }}
              onComplete=${createInstitution}
              error=${codeError}
              hint=${isDevCode ? t('auth.emailCode.devCodeHint') : t('auth.emailCode.hint')}
              disabled=${isSubmitting}
              autoFocus=${!isNameMissing}
            />
            <div class="auth-secondary-actions">
              <${ResendCodeButton} secondsLeft=${resendSecondsLeft} isSending=${isResending} onResend=${() => sendEmailCode({ isResend: true })} />
              <button type="button" class="auth-link-button" onClick=${() => {
                resetEmailCode();
                /** @type {HTMLInputElement | null} */ (document.querySelector('.auth-form input[name="email"]'))?.focus();
              }}>${t('auth.emailCode.changeEmail')}</button>
            </div>
          </div>`
        : null}
      ${error ? html`<div class="auth-error" role="alert">${error}</div>` : null}
      ${isCodeSent
        ? html`<${MainButton} type="submit" title=${t('auth.signUp.submit')} isLoading=${isSubmitting}
            disabled=${!canCreateInstitution(form, formState) || emailCode.length !== EMAIL_CODE_LENGTH} />`
        : html`<${MainButton} type="submit" title=${t('auth.signUp.sendCode')} isLoading=${isSubmitting} disabled=${!canSendCode} />`}
    </form>
  <//>`;
}
